/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useMediaQuery, Grid, Box, useTheme, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetOffersQuery, usePatchOferSelectedMutation } from "@api/marketPlaceApis";
import { ApiError } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import CustomRightImage from "@components/CustomRightImagev2";
import Carousel from "react-material-ui-carousel";
import CreditOfferCard from "@components/CreditOfferCard";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { LoanApplicationOfferResponse, PayloadRequestPatchOffer } from "@interfaces/MarketPlace";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setLoanStatus } from "@store/slices/registerSlice";
import TablaOfertas from "@components/TablaOfertas";
import ViewTabs from "@components/TabOfertas";
import { usePostDocumentGenarateMutation } from "@api/documents";

const NEXT_SATATUS = "COFAC";
const NEXT_SATATUS_COMPANY = "CAOAC";

const PATH_OFFER = "/producto/creditosimple/oferta_aceptada";
const PATH_OFFER_COMPANY = "/producto/creditosimple/oferta_aceptada_company";

const PATH_OFFER_WITH_GUARANTEE = "/producto/creditosimple/oferta_con_garantia";

function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const OfertasMarketplace: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [patchOfferSelected] = usePatchOferSelectedMutation();
  const [triggerPostDocumentGenerate] = usePostDocumentGenarateMutation();

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getOffersMarket] = useLazyGetOffersQuery();
  const [offersList, setOffersList] = useState<LoanApplicationOfferResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewType, setViewType] = useState<"cards" | "table">("cards");

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const responseOffers = await getOffersMarket({ id_sol: application.id }).unwrap();
        setOffersList(responseOffers);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const selectedOffer = async (offer: LoanApplicationOfferResponse) => {
    try {
      setIsLoading(true);
      const data: PayloadRequestPatchOffer = {
        accept: true,
      };
      await patchOfferSelected({ id_offer: offer.id, id_sol: application.id, data: data }).unwrap();

      if (offer.details?.withGuarantee) {
        dispatch(setSelectedOffer(offer));
        navigate(PATH_OFFER_WITH_GUARANTEE);
        return;
      }

      const nextStatus = offer.financial_company.is_partner ? NEXT_SATATUS_COMPANY : NEXT_SATATUS;
      const nextPath = offer.financial_company.is_partner ? PATH_OFFER_COMPANY : PATH_OFFER;

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(nextStatus));
      dispatch(setSelectedOffer(offer));
      navigate(nextPath);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "OfertasMarketplace-selectedOffer",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const groupedOffers = chunkArray(offersList, 3);

  const generateTableComparison = async () => {
    try {
      setIsLoading(true);
      const payload = {
        loan_application_id: application.id,
        document_type: "tabla_ofertas",
      };

      const response = await triggerPostDocumentGenerate(payload).unwrap();

      let base64Data = "";

      // Intentar diferentes formatos de respuesta
      if (typeof response === "string") {
        base64Data = response;
      } else if (response?.data) {
        base64Data = String(response.data);
      } else {
        throw new Error("No se recibió data válida");
      }

      if (base64Data) {
        // Limpiar espacios
        const cleanBase64 = base64Data.replace(/\s/g, "");

        // Convertir base64 a blob
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Crear URL temporal y descargar
        const url = window.URL.createObjectURL(blob);

        // Método 1: Link tradicional
        const link = document.createElement("a");
        link.href = url;
        link.download = `comparativa_ofertas_${new Date().toISOString().split("T")[0]}.pdf`;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();

        // Método 2: Forzar navegación (backup)
        setTimeout(() => {
          try {
            window.open(url, "_blank");
          } catch (e) {
            // Silencioso
          }
        }, 500);

        // Limpiar después de un delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 5000);
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "OfertasMarketplace-generateTableComparison",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <Typography
          sx={{ mb: 1 }}
          variant={isMobileOrTablet ? "h4" : "h3"}
          fontWeight={600}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          ¡Tenemos buenas noticias! <br />
          {company.name}
        </Typography>

        <Typography
          sx={{ mb: 1 }}
          variant={"body1"}
          fontWeight={500}
          color={theme?.palette?.primary?.main}
          textAlign={"center"}
        >
          Revisa los detalles, compara y elige la opción que mejor se adapte a tu negocio. <br />
          Recuerda: el tiempo de respuesta es clave para avanzar con el financiamiento.
        </Typography>

        <Box mt={2} mb={2} display="flex" justifyContent="center">
          {offersList.length > 3 && <ViewTabs onViewChange={setViewType} />}
        </Box>

        {offersList.length > 0 && (
          <Box mt={2} mb={2} display="flex" justifyContent="center">
            <Typography
              fontWeight={700}
              color={isLoading ? theme?.palette?.grey?.[400] : theme?.palette?.primary?.main}
              variant="body2"
              sx={{
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: isLoading ? 0.6 : 1,
              }}
              onClick={!isLoading ? generateTableComparison : undefined}
            >
              Descargar comparativa de Ofertas
              <ExitToAppIcon style={{ transform: "rotate(90deg)", fontSize: "20px" }} />
            </Typography>
          </Box>
        )}

        {viewType === "table" && (
          <Box mx={3} my={2}>
            {!isLoading ? (
              <TablaOfertas offer={offersList} />
            ) : (
              <Skeleton variant="rectangular" animation="wave" width="100%" height={400} />
            )}
          </Box>
        )}

        {viewType === "cards" && (
          <>
            {!isMobileOrTablet && (
              <>
                {!isLoading ? (
                  <Carousel
                    indicators={true}
                    activeIndicatorIconButtonProps={{
                      style: {
                        color: theme?.palette?.primary?.accent,
                        marginTop: "10px",
                      },
                    }}
                    indicatorIconButtonProps={{
                      style: {
                        marginTop: "10px",
                        color: theme?.palette?.primary?.light,
                      },
                    }}
                    sx={{ mb: 2 }}
                  >
                    {groupedOffers.map((group, groupIndex) => (
                      <Box key={groupIndex} display="flex" justifyContent="center" pt={2}>
                        <Grid container spacing={2} justifyContent="center" alignItems="stretch" mx={1}>
                          {group.map((offer, index) => (
                            <Grid item xs={12} sm={6} md={4} key={offer.id} sx={{ display: "flex" }}>
                              <Box
                                sx={{
                                  boxShadow: "8px 4px 20px 0px #58585840",
                                  borderRadius: 10,
                                  paddingBottom: "10px",
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <CreditOfferCard
                                  offer={offer}
                                  offers_length={offersList.length}
                                  showButton
                                  onSelectOffer={() => selectedOffer(offer)}
                                  index={index}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))}
                  </Carousel>
                ) : (
                  <Grid container spacing={2} justifyContent="center">
                    {[...Array(3)].map((_, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box mx={2}>
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            width="100%"
                            height={isMobileOrTablet ? 150 : 600}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {isMobileOrTablet && (
              <>
                {!isLoading ? (
                  <Box
                    ml={4}
                    mr={4}
                    mt={3}
                    mb={4}
                    sx={{
                      boxShadow: "8px 4px 20px 0px #58585840",
                      borderRadius: 10,
                      paddingBottom: "10px",
                    }}
                  >
                    <Carousel
                      indicators={true}
                      activeIndicatorIconButtonProps={{
                        style: {
                          color: theme?.palette?.primary?.accent,
                          marginTop: "10px",
                        },
                      }}
                      indicatorIconButtonProps={{
                        style: {
                          marginTop: "10px",
                          color: theme?.palette?.primary?.light,
                        },
                      }}
                      sx={{ mb: 2 }}
                    >
                      {offersList.map((offer, index) => (
                        <CreditOfferCard
                          key={offer.id}
                          offer={offer}
                          offers_length={offersList.length}
                          showButton={true}
                          onSelectOffer={() => selectedOffer(offer)}
                          index={index}
                        />
                      ))}
                    </Carousel>
                  </Box>
                ) : (
                  <Skeleton variant="rectangular" animation="wave" width="100%" height={isMobileOrTablet ? 150 : 600} />
                )}
              </>
            )}
          </>
        )}
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default OfertasMarketplace;
